import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  OnFarmImplantRequestError,
  type OnFarmImplantSessionEmbryo,
  useRecordOnFarmImplantFrozenDiscard,
} from '@/services/on-farm-implant';

type DiscardFrozenDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  embryos: OnFarmImplantSessionEmbryo[];
  onDiscarded: () => void;
};

export const DiscardFrozenDialog = ({
  open,
  onOpenChange,
  sessionId,
  embryos,
  onDiscarded,
}: DiscardFrozenDialogProps) => {
  const { t } = useTranslation();

  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: recordFrozenDiscard, isPending } =
    useRecordOnFarmImplantFrozenDiscard();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setProblems([]);
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (embryos.length === 0) return;

    setProblems([]);

    try {
      const result = await recordFrozenDiscard({
        sessionId,
        input: {
          items: embryos.map((embryo) => ({
            sessionEmbryoId: embryo.sessionEmbryoId,
          })),
          versions: collectVersions(embryos),
        },
      });

      handleOpenChange(false);
      onDiscarded();

      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.frozen.discardFrozen.success', {
          count: result.totalDiscarded,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to record the frozen discard:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.frozen.discardFrozen.failed'),
      });
    }
  }, [
    embryos,
    handleOpenChange,
    onDiscarded,
    recordFrozenDiscard,
    sessionId,
    t,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.frozen.discardFrozen.title')}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.frozen.discardFrozen.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="py-1 font-medium">
                  {t('onFarmImplant.frozen.embryo')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.frozen.frozenStraw')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.frozen.cane')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.frozen.discardFrozen.grade')}
                </th>
              </tr>
            </thead>
            <tbody>
              {embryos.map((embryo) => (
                <tr key={embryo.sessionEmbryoId} className="border-t">
                  <td className="py-1.5">{embryo.embryoName ?? '—'}</td>
                  <td className="py-1.5">{embryo.frozenStrawNumber ?? '—'}</td>
                  <td className="py-1.5">{embryo.cane ?? '—'}</td>
                  <td className="py-1.5">{embryo.grade ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-xs text-gray-500">
            {t('onFarmImplant.frozen.discardFrozen.permanentWarning')}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={embryos.length === 0 || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.frozen.discardFrozen.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
